import { purchase } from 'js-api-client';
import { camelizeKeys } from 'humps';
import getPaymentEngineAndProvider from 'utils/getPaymentEngineAndProvider';
import { requestPayment, updatePurchase } from './purchase';
import { paymentFailureHandler, paymentPollingHandler, getPaymentPayload } from './payment';
import applePayFactory from '../../payments/core/factories/applepay';

/**
 * Creates the polling handler with async coordination support.
 * Determines success/failure based on polling response and calls appropriate callback.
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {string} purchaseToken - The purchase token.
 * @param {Function} [onPaymentSuccess] - Optional success callback.
 * @param {Function} [onPaymentFailure] - Optional failure callback.
 * @returns {Function} The polling handler function.
 */
function createPollingHandler(dispatch, purchaseToken, onPaymentSuccess, onPaymentFailure) {
  return (response) => {
    const { status, payload } = camelizeKeys(response);
    const { purchaseState: pollingPurchaseState } = payload;

    // Determine if payment was successful
    // Success: purchaseState is no longer 'attempt' (e.g., 'completed', 'paid')
    const isSuccess = pollingPurchaseState !== 'attempt';

    // Determine if payment failed
    // Failure: status is not pending/completed, or payload.status is 'failed'/'rejected'
    const isFailure =
      (!['pending', 'completed'].includes(status) ||
        ['failed', 'rejected'].includes(payload.status)) &&
      !isSuccess;

    // Call the standard polling handler to dispatch Redux actions
    paymentPollingHandler(dispatch, purchaseToken, response);

    // Notify async coordination callbacks
    if (isSuccess && typeof onPaymentSuccess === 'function') {
      onPaymentSuccess();
    } else if (isFailure && typeof onPaymentFailure === 'function') {
      onPaymentFailure();
    }
    // Note: If status is 'pending' and purchaseState remains 'attempt', neither callback is invoked.
    // In this case, the hook (useAdyenApplePay) will reject on timeout and the UI will display
    // a confirming state. This relates to the async flow handled by paymentPollingHandler, which
    // dispatches Redux actions for state updates, while onPaymentSuccess/onPaymentFailure callbacks
    // coordinate with the Apple Pay session. Neither callback is called here because the payment
    // outcome is still undetermined—the polling continues via paymentPollingHandler until resolved.
  };
}

/**
 * Creates the error handler with async coordination support.
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Function} [onPaymentFailure] - Optional failure callback.
 * @returns {Function} The error handler function.
 */
function createErrorHandler(dispatch, onPaymentFailure) {
  return (error) => {
    paymentFailureHandler(dispatch, error);

    // Notify failure callback for async coordination
    if (typeof onPaymentFailure === 'function') {
      onPaymentFailure();
    }
  };
}

/**
 * Initiates a payment attempt using Apple Pay.
 *
 * Supports async coordination with Apple Pay session via optional callbacks.
 * When callbacks are provided, they are called based on payment polling result:
 * - onPaymentSuccess: Called when payment completes successfully (purchaseState !== 'attempt')
 * - onPaymentFailure: Called when payment fails or encounters an error
 *
 * @param {Object} applePayData - applePayPayload, purchaseToken, onPaymentSuccess, onPaymentFailure.
 * @param {Object} applePayData.applePayPayload - The payload from Apple Pay SDK (state data, token, etc.).
 * @param {string} applePayData.purchaseToken - The token associated with the purchase.
 * @param {Function} [applePayData.onPaymentSuccess] - Optional callback for successful payment.
 * @param {Function} [applePayData.onPaymentFailure] - Optional callback for failed payment.
 * @returns {Function} A thunk action that dispatches actions based on the Apple Pay payment process.
 */
export function applePayPaymentAttempt(applePayData) {
  const { applePayPayload, purchaseToken, onPaymentSuccess, onPaymentFailure } = applePayData;

  return async (dispatch, getState) => {
    dispatch(updatePurchase(true));
    dispatch(requestPayment());

    // Get payment method configuration from state
    const { purchase: purchaseState } = getState();
    const selectedPaymentMethod = purchaseState.get('selectedPaymentMethod');
    const { engine: paymentEngine, provider: paymentProvider } = getPaymentEngineAndProvider(
      selectedPaymentMethod,
      { defaultEngine: 'adyen', defaultProvider: 'apple_pay' },
    );

    const handlePaymentPolling = createPollingHandler(
      dispatch,
      purchaseToken,
      onPaymentSuccess,
      onPaymentFailure,
    );
    const handlePaymentError = createErrorHandler(dispatch, onPaymentFailure);

    try {
      // Create Apple Pay instance using factory
      const applePayPayment = applePayFactory.create({ engine: paymentEngine });

      // Create payload using strategy
      const enginePayload = await applePayPayment.createPayload(applePayPayload);

      // Combine with base payment payload
      const paymentPayload = {
        ...getPaymentPayload('apple_pay', paymentEngine),
        payment_provider: paymentProvider,
        ...enginePayload,
      };

      // Send to backend with async coordination support
      await purchase
        .createPayment(purchaseToken, paymentPayload, {
          onReceivePayment: handlePaymentPolling,
        })
        .catch(handlePaymentError);
    } catch (error) {
      handlePaymentError(error);
    }
  };
}

export default applePayPaymentAttempt;
